import React, { useMemo, useId } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

/**
 * LifetimeMetricCardProps - Interface for the LifetimeMetricCard component
 * 
 * This interface defines all the props accepted by the LifetimeMetricCard component,
 * which displays a single metric with its label, value, unit, and description.
 * 
 * Formatting Guidelines:
 * - Count values: Use .toLocaleString() for thousands separators (e.g., "1,234,567")
 * - Percentage values: Use .toFixed(1) for one decimal place, displayed with unit="%" (e.g., "98.5")
 * - Decimal numbers: Use .toFixed(1) for consistency (e.g., "1.4")
 * - Unit symbol: Use "%" for percentages, not "percent"
 */
export interface LifetimeMetricCardProps {
  /** The label or title for the metric (e.g., "Total Emails Sent") */
  label: string;
  
  /** 
   * The numeric or string value to display (e.g., "1,234", "98.5", 1234)
   * 
   * For string values, ensure proper formatting:
   * - Count values should use .toLocaleString() for thousands separators
   * - Percentage values should use .toFixed(1) formatting
   * - All decimal values should use .toFixed(1) for consistency
   */
  value: string | number;
  
  /** 
   * Optional unit to display alongside the value (e.g., "%", "emails")
   * For percentages, always use "%" (not "percent")
   */
  unit?: string;
  
  /** Optional description or additional context for the metric */
  description?: string;
  
  /** Show data collection overlay when true */
  isPending?: boolean;
}


/**
 * LifetimeMetricCard Component
 * 
 * A reusable component that displays a single metric with appropriate styling and accessibility.
 * The component renders a Material-UI Paper with elevation for visual separation.
 * 
 * @param props - LifetimeMetricCardProps
 * @returns React component displaying the metric card
 */

export const LifetimeMetricCard: React.FC<LifetimeMetricCardProps> = ({
  label,
  value,
  unit,
  description,
  isPending,
}) => {
  const labelId = useId();
  const descriptionId = useId();
  const valueId = useId();
  
  // Create accessible label that includes metric information
  const accessibleLabel = useMemo(() => {
    return `${label}: ${value}${unit ? ` ${unit}` : ''}`;
  }, [label, value, unit]);

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        borderRadius: '8px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
      role="region"
      aria-label={accessibleLabel}
      aria-describedby={description ? descriptionId : undefined}
    >
      <Typography
        id={labelId}
        variant="metricLabel"
        component="div"
        sx={{ mb: 1 }}
      >
        {label}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: description ? 1 : 0 }}>
        <Typography
          id={valueId}
          variant="metricValue"
          component="div"
        >
          {value}
        </Typography>

        {unit && (
          <Typography
            variant="metricUnit"
            component="span"
            sx={{ ml: 1 }}
            aria-label={unit}
          >
            {unit}
          </Typography>
        )}
      </Box>

      {description && (
        <Typography
          id={descriptionId}
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 400,
            mt: 'auto',
          }}
        >
          {description}
        </Typography>
      )}

      {isPending && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            zIndex: 1,
          }}
          role="status"
          aria-live="polite"
          aria-label="Data is being collected"
        >
          <HourglassEmptyIcon 
            sx={{ 
              fontSize: '2rem', 
              color: 'primary.main',
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 },
              },
            }}
            aria-hidden="true"
          />
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              fontWeight: 500,
              textAlign: 'center',
              px: 2,
            }}
          >
            Collecting data...
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default LifetimeMetricCard;
